import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Select,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { FiSearch, FiSliders, FiX } from "react-icons/fi";
import { useSession } from "./session";
import CharacterCard from "./CharacterCard";
import Pagination from "./Pagination";
import HeroCarousel from "./HeroCarousel";
const scrollPositions = new Map();
export default function HomePage() {
  const { api, auth } = useSession(),
    [params, setParams] = useSearchParams(),
    location = useLocation();
  const q = params.get("q") || "",
    tag = params.get("tag") || "",
    sort = params.get("sort") || (q ? "relevance" : "popular"),
    page = Math.max(1, Math.min(100, parseInt(params.get("page"), 10) || 1));
  const [keyword, setKeyword] = useState(q),
    [results, setResults] = useState([]),
    [categories, setCategories] = useState([]),
    [hasMore, setHasMore] = useState(false),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [filters, setFilters] = useState(false),
    [retry, setRetry] = useState(0);
  const scrollKey = location.pathname + location.search,
    restored = useRef(false);
  const [loadedKey, setLoadedKey] = useState("");
  const sentinel = useRef(null),
    [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => setKeyword(q), [q]);
  const update = (values) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([k, v]) => {
      v ? next.set(k, String(v)) : next.delete(k);
    });
    if (!("page" in values)) next.delete("page");
    setParams(next);
  };
  useLayoutEffect(() => {
    restored.current = false;
    const save = () => {
      if (restored.current) scrollPositions.set(scrollKey, window.scrollY);
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [scrollKey]);
  useEffect(() => {
    if (auth.isLoading) return;
    const abort = new AbortController();
    setLoading(true);
    setError("");
    const query = new URLSearchParams({
      q,
      tag,
      sort,
      page: String(page),
    });
    api(`/api/search?${query}`, { signal: abort.signal })
      .then((r) => r.json())
      .then((d) => {
        if (abort.signal.aborted) return;
        setResults(d.results);
        setHasMore(d.hasMore);
        setLoadedKey(scrollKey);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setError(e.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [api, auth.isLoading, q, tag, sort, page, retry, scrollKey]);
  useEffect(() => {
    if (!loading && loadedKey === scrollKey && !restored.current) {
      restored.current = true;
      window.scrollTo({ top: scrollPositions.get(scrollKey) || 0 });
    }
  }, [loading, loadedKey, scrollKey]);
  useEffect(() => {
    if (auth.isLoading) return;
    const abort = new AbortController();
    api("/api/categories", { signal: abort.signal })
      .then((r) => r.json())
      .then((d) => setCategories(d.categories.map((t) => t.tag)))
      .catch(() => {});
    return () => abort.abort();
  }, [api, auth.isLoading]);
  const submit = (e) => {
    e.preventDefault();
    update({ q: keyword.trim(), sort: "" });
  };
  return (
    <div className="discover-page has-sticky-search">
      <div ref={sentinel} className="search-sentinel" aria-hidden="true" />
      <div className={`search-sticky${stuck ? " is-stuck" : ""}`}>
        <form className="search-bar" onSubmit={submit}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <FiSearch />
            </InputLeftElement>
            <Input
              aria-label="Search characters or stories"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search characters or stories"
            />
            <Button className="search-submit" type="submit" variant="brand">
              Search
            </Button>
          </InputGroup>
        </form>
      </div>
      <div className={`hero${q ? "" : " has-carousel"}`}>
        <div className="page-heading">
          <p className="eyebrow">DISCOVER</p>
          <h1>{q ? `Results for “${q}”` : "Find your next story"}</h1>
          <p>Characters worth getting to know.</p>
        </div>
        {!q && <HeroCarousel />}
      </div>
      <div className="browse-toolbar">
        <div className="sort-tabs" role="group" aria-label="Sort characters">
          {[
            ...(q ? [["relevance", "Relevant"]] : []),
            ["popular", "Popular"],
            ["newest", "Newest"],
            ["likes", "Most liked"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={sort === key ? "selected" : ""}
              aria-pressed={sort === key}
              onClick={() => update({ sort: key })}
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          leftIcon={<FiSliders />}
          onClick={() => setFilters(true)}
        >
          Filters
        </Button>
      </div>
      <div className="category-row" aria-label="Character categories">
        <button
          className={`category-chip${!tag ? " selected" : ""}`}
          onClick={() => update({ tag: "" })}
        >
          All
        </button>
        {categories.slice(0, 8).map((t) => (
          <button
            key={t}
            className={`category-chip${tag === t ? " selected" : ""}`}
            onClick={() => update({ tag: tag === t ? "" : t })}
          >
            {t}
          </button>
        ))}
        {tag && !categories.slice(0, 8).includes(tag) && (
          <button
            className="category-chip selected"
            onClick={() => update({ tag: "" })}
          >
            {tag}
            <FiX />
          </button>
        )}
      </div>
      {error ? (
        <div className="empty-state" role="alert">
          <h2>Could not load characters</h2>
          <p>{error}</p>
          <Button onClick={() => setRetry((v) => v + 1)}>Try again</Button>
        </div>
      ) : loading ? (
        <div
          className="character-grid"
          aria-label="Loading characters"
          aria-busy="true"
        >
          {Array.from({ length: 10 }, (_, i) => (
            <Skeleton
              key={i}
              className="card-skeleton"
              borderRadius="16px"
              startColor="subtle"
              endColor="line"
            />
          ))}
        </div>
      ) : results.length ? (
        <>
          <div className="character-grid">
            {results.map((c, i) => (
              <CharacterCard key={c.publicId} character={c} index={i} />
            ))}
          </div>
          <Pagination
            page={page}
            hasMore={hasMore}
            onPageChange={(p) => update({ page: p })}
          />
        </>
      ) : (
        <div className="empty-state">
          <FiSearch />
          <h2>No characters found</h2>
          <p>Try a different search or clear your filters.</p>
          <Button
            onClick={() => {
              setKeyword("");
              setParams({});
            }}
          >
            Clear filters
          </Button>
        </div>
      )}
      <Drawer
        isOpen={filters}
        onClose={() => setFilters(false)}
        placement="right"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Refine your discovery</DrawerHeader>
          <DrawerBody>
            <FormControl mb={6}>
              <FormLabel>Sort by</FormLabel>
              <Select
                value={sort}
                onChange={(e) => update({ sort: e.target.value })}
              >
                {q && <option value="relevance">Relevance</option>}
                <option value="popular">Popular</option>
                <option value="newest">Newest</option>
                <option value="likes">Most liked</option>
                <option value="visits">Most visited</option>
              </Select>
            </FormControl>
            <FormControl mb={6}>
              <FormLabel>Category</FormLabel>
              <Select
                value={tag}
                onChange={(e) => update({ tag: e.target.value })}
              >
                <option value="">All categories</option>
                {categories.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </FormControl>
          </DrawerBody>
          <DrawerFooter>
            <Button variant="brand" onClick={() => setFilters(false)}>
              Show characters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
